/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Bell } from 'lucide-react';
import { cn } from '@/lib/cn';
import { isActionable } from './push';
import { usePushUi } from './PushProvider';

/**
 * **Push notifications**, in the account menu, with a switch that says where
 * this device stands.
 *
 * The row shows the state because "did I already turn this on?" is the question
 * people will actually have, and a switch answers it without anybody having to
 * open anything.
 *
 * **But the switch is only live where flipping it can do something.** Blocked,
 * an iPhone in a Safari tab, an unsupported browser — in each of those the
 * switch is dimmed and pressing the row opens the dialog, which explains. A
 * switch that flips back by itself is how somebody decides a feature is broken,
 * and on an iOS Safari tab that is exactly what a permission prompt produces:
 * nothing at all.
 *
 * | State | The row | Pressing it |
 * |---|---|---|
 * | on | switch on | turns it off, there and then |
 * | off | switch off, live | asks permission, there and then |
 * | blocked / iOS tab / unsupported | switch off, dimmed | opens the dialog |
 */
export function PushRow() {
  const push = usePushUi();

  // Outside the provider — a fragment mounted in isolation. Nothing to draw.
  if (!push) return null;
  // Nothing while the answer is being worked out: a row that appears saying
  // "off" and corrects itself to "on" a moment later is worse than one that
  // arrives a moment late, because the wrong answer is the one people act on.
  if (push.loading) return null;

  const live = isActionable(push.state);
  const on = push.state === 'on';

  const press = () => {
    if (!live) return push.explain();
    if (on) void push.disable();
    else void push.enable();
  };

  return (
    <div className="push-row-group mb-1 border-b border-border pb-1">
      <div
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-semibold text-ink transition-colors duration-fast ease-out-soft hover:bg-bg',
          push.busy && 'opacity-60',
        )}
      >
        <Bell className="size-[15px] shrink-0 text-ink-faint" aria-hidden />

        {/* **The label opens the explanation in every state**, including the two
            where the switch works. Somebody who wants to know what this is —
            or who needs to tell a colleague with an iPhone — must not have to
            turn it off to find out. */}
        <button
          id="push-row"
          type="button"
          onClick={() => push.explain()}
          className="tap min-w-0 flex-1 truncate text-left font-semibold text-ink"
        >
          Push notifications
        </button>

        {/* **A switch only where it is one.**

            Where flipping it works, this is a real `role="switch"` with
            `aria-checked`, which is what makes a screen reader say "Push
            notifications, switch, on" rather than reading a button with no
            state.

            Where it does not work it is **not a switch at all** — it is a
            button that opens the explanation, and it says so. The first version
            of this kept `role="switch"` and added `aria-disabled`, which is a
            contradiction rather than a compromise: it told assistive technology
            the control was inoperable while the entire point of it was that
            pressing it did something. Anything obeying that attribute — a
            screen reader, and Playwright, which is how this was caught — would
            have refused the one interaction available.

            `data-live` is what carries "this is dimmed" to the tests, because
            the dimming is a class and no check in this repo can see a class. */}
        <button
          id="push-toggle"
          type="button"
          {...(live
            ? { role: 'switch' as const, 'aria-checked': on, 'aria-label': 'Push notifications on this device' }
            : { 'aria-label': 'Why push notifications are unavailable on this device' })}
          data-live={live}
          data-push-state={push.state}
          disabled={push.busy}
          onClick={press}
          className={cn(
            'tap relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-base ease-out-soft',
            on ? 'border-action bg-action' : 'border-border-strong bg-bg',
            // Dimmed, not hidden. The state it reports — off — is true; what it
            // cannot do is change it, and the dialog is one press away.
            !live && 'opacity-45',
          )}
        >
          <span
            className={cn(
              'absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-surface shadow-sm transition-[left] duration-base ease-out-soft',
              on ? 'left-[18px]' : 'left-[2px]',
            )}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
